/***
 * This is a copy of the Backbone sync implementation which performs some url-rewriting for backbone calls.
 */
import _ from 'underscore';
import Backbone from 'backbone';
import 'config/request-header';
import DiTokenManager from 'auth/DiTokenManager';
import { isReadonlyMode } from '../../../system/SystemUtil';

function urlRewrite(url) {
    if (url.substr(0, 7) == '/proxy/') {
        return '/modern' + url;
    } else {
        return url;
    }
}

var methodMap = {
    create: 'POST',
    update: 'PUT',
    patch: 'PATCH',
    delete: 'DELETE',
    read: 'GET'
};

var CustomHeaderRequests = ['POST', 'PUT', 'DELETE', 'PATCH'];

Backbone._sync = function(method, model, options) {
    var type = methodMap[method];

    var emulateHTTP = Backbone.emulateHTTP;
    if (typeof model.emulateHTTP !== 'undefined') {
        emulateHTTP = model.emulateHTTP;
    }

    // Default options, unless specified.
    _.defaults(options || (options = {}), {
        emulateHTTP: emulateHTTP,
        emulateJSON: Backbone.emulateJSON
    });

    // Default JSON-request options.
    var params = { type: type, dataType: 'json' };

    // Ensure that we have a URL.
    if (!options.url) {
        params.url = urlRewrite(_.result(model, 'url')) || urlError();
    } else {
        params.url = urlRewrite(options.url) || urlError();
        options.url = params.url;
    }

    if (model.backend === 'ghs') {
        const diBackend = window.DI_BACKENDS && window.DI_BACKENDS[model.backend];
        params.url = `https://${diBackend}${params.url}`;
        if (options.url) {
            options.url = `https://${diBackend}${params.url}`;
        }
        options.xhrFields = {
            withCredentials: true
        };
    }

    // Ensure that we have the appropriate request data.
    if (options.data == null && model && (method === 'create' || method === 'update' || method === 'patch')) {
        params.contentType = 'application/json';
        params.data = JSON.stringify(options.attrs || model.toJSON(options));
    }

    // For older servers, emulate JSON by encoding the request into an HTML-form.
    if (options.emulateJSON) {
        params.contentType = 'application/x-www-form-urlencoded';
        params.data = params.data ? { model: params.data } : {};
    }

    // For older servers, emulate HTTP by mimicking the HTTP method with `_method`
    // And an `X-HTTP-Method-Override` header.
    if (options.emulateHTTP && (type === 'PUT' || type === 'DELETE' || type === 'PATCH')) {
        params.type = 'POST';
        if (options.emulateJSON) params.data._method = type;
        let beforeSend = options.beforeSend;
        options.beforeSend = function(xhr) {
            xhr.setRequestHeader('X-HTTP-Method-Override', type);
            if (beforeSend) {
                return beforeSend.apply(this, arguments);
            }
        };
    }

    if (CustomHeaderRequests.includes(type)) {
        let beforeSend = options.beforeSend;
        options.beforeSend = function(xhr) {
            xhr.setRequestHeader('NK', 'NT');
            if (beforeSend) {
                return beforeSend.apply(this, arguments);
            }
        };
    }

    // Don't process data on a non-GET request.
    if (params.type !== 'GET' && !options.emulateJSON) {
        params.processData = false;
    }

    // If we're sending a `PATCH` request, and we're in an old Internet Explorer
    // that still has ActiveX enabled by default, override jQuery to use that
    // for XHR instead. Remove this line when jQuery supports `PATCH` on IE8.
    if (
        params.type === 'PATCH' &&
        window.ActiveXObject &&
        !(window.external && window.external.msActiveXFilteringEnabled)
    ) {
        params.xhr = function() {
            return new ActiveXObject('Microsoft.XMLHTTP');
        };
    }

    // Make the request, allowing the user to override any Ajax options.
    var xhr = (options.xhr = Backbone.ajax(_.extend(params, options)));
    model.trigger('request', model, xhr, options);
    return xhr;
};

// New sync to add async logic before calling the original sync
Backbone.sync = function(method, model, options) {
    // Read Only mode should not trigger any POST PUT DELETE. We'll only allow GET requests.
    if (isReadonlyMode() && method === 'update') {
        return;
    }

    if (model.backend) {
        // Async IIFE (Immediately Invoked Function Expression) to allow the use of await
        // Need to call original sync Backbone._sync since we can't await for this block to complete.
        (async () => {
            const token = await DiTokenManager.getToken();

            const diBackend = window.DI_BACKENDS && window.DI_BACKENDS[model.backend];
            let beforeSend = options.beforeSend;
            options.beforeSend = function(xhr, settings) {
                xhr.setRequestHeader('DI-Backend', diBackend);
                xhr.setRequestHeader('Authorization', `Bearer ${token}`);

                if (beforeSend) {
                    return beforeSend.apply(this, arguments);
                }
            };

            Backbone._sync.apply(this, arguments);
        })();
    } else {
        return Backbone._sync.apply(this, arguments);
    }
};

export default Backbone.sync;
